import { useEffect, useRef, useState, useCallback } from "react";
import { tr } from "../i18n/tr";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/** Reusable chat panel for a formation session (trainer ↔ students). */
export default function SessionChat({ sessionId, api, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const lastPollRef = useRef(null);

  const load = useCallback(async (since) => {
    try {
      const params = since ? { since } : {};
      const r = await api.get("/lms/sessions/" + sessionId + "/chat", { params });
      if (since) {
        setMessages((m) => [...m, ...(r.data || [])]);
      } else {
        setMessages(r.data || []);
      }
      if (r.data?.length) {
        lastPollRef.current = r.data[r.data.length - 1].created_at;
      }
    } catch (e) { if (!e.offline) toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [sessionId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sessionId]);

  // Poll every 20s for new messages (incremental)
  useEffect(() => {
    const id = setInterval(() => {
      load(lastPollRef.current || undefined);
    }, 20000);
    return () => clearInterval(id);
  // eslint-disable-next-line
  }, [sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const r = await api.post("/lms/sessions/" + sessionId + "/chat", { text: t });
      const msg = r.queued
        ? { message_id: r.data.client_id, text: t, author_id: currentUser?.user_id, author_name: currentUser?.name || "Moi", author_role: "moi", created_at: r.data.created_at, pending: true }
        : r.data;
      setMessages((m) => [...m, msg]);
      api.patchCache?.("/lms/sessions/" + sessionId + "/chat", (list) => [...(list || []), msg]);
      setText("");
      if (!r.queued) lastPollRef.current = r.data.created_at;
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSending(false);
  };

  if (loading) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>;

  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-xl flex flex-col" style={{ height: "60vh" }} data-testid="session-chat">
      <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-[#f7c935]" />
        <div>
          <h3 className="text-sm font-bold text-white">{tr("lms.messagerie_de_la_session_29b1", "Messagerie de la session")}</h3>
          <p className="text-[10px] text-slate-500">{tr("lms.formateurs_et_stagiaires_peuvent_5a9e", "Formateurs et stagiaires peuvent echanger ici.")}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-xs text-slate-600 py-10">{tr("lms.pas_encore_de_message_3d3e", "Pas encore de message. Soyez le premier !")}</div>
        )}
        {messages.map((m) => {
          const mine = m.author_id && currentUser && m.author_id === currentUser.user_id;
          return (
            <div key={m.message_id} className={"flex " + (mine ? "justify-end" : "justify-start")} data-testid={"chat-msg-" + m.message_id}>
              <div className={"max-w-[75%] rounded-xl px-3 py-2 " + (mine ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-white")}>
                {!mine && (
                  <div className="text-[10px] font-bold mb-0.5 opacity-75">
                    {m.author_name} {m.author_role === "trainer" && "· Formateur"}{m.author_role === "admin" && "· Admin"}
                  </div>
                )}
                <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                <div className={"text-[9px] mt-0.5 " + (mine ? "text-[#14101c]/60" : "text-slate-500")}>{new Date(m.created_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}{m.pending && <span className="ml-1 italic" data-testid="chat-pending">{tr("lms.en_attente_de_synchro_e255", "· en attente de synchro")}</span>}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-white/5 p-3 flex gap-2">
        <Input placeholder={tr("lms.ecrire_un_message_d979", "Ecrire un message...")} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(); }} className="bg-[#14101c] border-white/10 text-white" data-testid="chat-input" />
        <Button onClick={send} disabled={sending || !text.trim()} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="chat-send"><Send className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}
